import {
  Bell,
  BellRing,
  CheckCheck,
  Hourglass,
  MessageSquare,
  PackageCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '../../../shared/lib/utils'
import { useAvisos, useMarcarAvisoLido, useMarcarTodosLidos } from '../hooks/useAvisos'
import { destinoDoAviso } from '../lib/destino'

const ICONE = {
  relato_triado: Sparkles,
  relato_entregue: PackageCheck,
  relato_mensagem: MessageSquare,
  progresso_pedido: Hourglass,
  progresso_cobranca: BellRing,
  progresso_publicado: TrendingUp,
}

function quando(iso) {
  const agora = Date.now()
  const alvo = new Date(iso).getTime()
  const minutos = Math.max(0, Math.round((agora - alvo) / 60000))
  if (minutos < 1) return 'agora'
  if (minutos < 60) return `há ${minutos} min`
  const horas = Math.round(minutos / 60)
  if (horas < 24) return `há ${horas} h`
  const dias = Math.round(horas / 24)
  return dias === 1 ? 'ontem' : `há ${dias} dias`
}

/**
 * ADR-084 — o sino do cliente.
 *
 * Mostra o que a equipe fez no chamado dele: o desfecho da triagem, a entrega e
 * a resposta escrita. Clicar leva ao chamado e marca como lido, porque aviso que
 * exige dois gestos para sumir vira ruído permanente.
 */
/**
 * `alinhar` decide de que lado o painel cai. No header do celular o sino fica na
 * direita da tela e o painel abre para a esquerda; na sidebar do desktop é o
 * contrário, e sem isso o painel abre para fora da janela (visto no retrato).
 */
export default function SinoDeAvisos({ className, alinhar = 'direita' }) {
  const { data: avisos = [] } = useAvisos()
  const marcarUm = useMarcarAvisoLido()
  const marcarTodos = useMarcarTodosLidos()
  const [aberto, setAberto] = useState(false)
  const caixa = useRef(null)
  const navigate = useNavigate()

  const naoLidos = avisos.filter((a) => !a.read).length

  useEffect(() => {
    if (!aberto) return
    const fora = (e) => {
      if (caixa.current && !caixa.current.contains(e.target)) setAberto(false)
    }
    document.addEventListener('mousedown', fora)
    return () => document.removeEventListener('mousedown', fora)
  }, [aberto])

  const abrirAviso = (aviso) => {
    if (!aviso.read) marcarUm.mutate(aviso.id)
    setAberto(false)
    const destino = destinoDoAviso(aviso)
    if (destino) navigate(destino)
  }

  return (
    <div ref={caixa} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label={naoLidos > 0 ? `Avisos: ${naoLidos} não lidos` : 'Avisos'}
        data-testid="sino-avisos"
        className="relative rounded-xl p-2 text-foreground hover:bg-white/12 transition-colors"
      >
        <Bell className="h-5 w-5" />
        {naoLidos > 0 && (
          <span
            data-testid="sino-badge"
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9.5px] font-bold text-white"
          >
            {naoLidos > 9 ? '9+' : naoLidos}
          </span>
        )}
      </button>

      {aberto && (
        <div
          data-testid="sino-painel"
          className={cn(
            'absolute z-50 mt-2 w-[330px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl bg-white beautiful-shadow border border-black/5',
            alinhar === 'esquerda' ? 'left-0' : 'right-0',
          )}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
            <span className="text-[12.5px] font-bold text-card-foreground">Avisos</span>
            {naoLidos > 0 && (
              <button
                type="button"
                onClick={() => marcarTodos.mutate()}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar tudo como lido
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {avisos.length === 0 ? (
              <p className="px-4 py-6 text-center text-[12px] text-muted-foreground">
                Nada por aqui ainda. Quando a equipe mexer num chamado seu ou precisar de você no
                Progresso, o aviso aparece aqui.
              </p>
            ) : (
              avisos.map((aviso) => {
                const Icone = ICONE[aviso.type] || Sparkles
                return (
                  <button
                    key={aviso.id}
                    type="button"
                    onClick={() => abrirAviso(aviso)}
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-black/[0.03]',
                      !aviso.read && 'bg-primary/[0.045]',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                        aviso.read
                          ? 'bg-black/5 text-muted-foreground'
                          : 'bg-primary/12 text-primary',
                      )}
                    >
                      <Icone className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[12.5px] font-semibold leading-snug text-card-foreground">
                        {aviso.title}
                      </span>
                      {aviso.body && (
                        <span className="mt-0.5 block truncate text-[11.5px] text-muted-foreground">
                          {aviso.body}
                        </span>
                      )}
                      <span className="mt-1 block text-[10.5px] text-muted-foreground/70">
                        {quando(aviso.created_at)}
                      </span>
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
